import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { noop } from '@prisjakt/ui-base';
import Spinner from '@prisjakt/ui-spinner';
import { Variant } from './types';

import * as S from './styles';

type Props = {
  className?: string;
  isSubscribed: boolean;
  title: React.ReactNode;
  description: React.ReactNode;
  buttonText: React.ReactNode;
  legalText: React.ReactNode;
  email?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  feedbackText: React.ReactNode;
  buttonClass: string;
  subscribeTextTitle: React.ReactNode;
  subscribeTextSubTitle?: React.ReactNode;
  stacked: boolean;
  variant: Variant;
  loading: boolean;
};

const SignupForm = (props: Props) => {
  const {
    className,
    isSubscribed,
    onSubmit = noop,
    onChange = noop,
    title,
    description,
    buttonText,
    feedbackText,
    legalText,
    buttonClass,
    stacked,
    variant,
    subscribeTextTitle,
    subscribeTextSubTitle,
    loading,
  } = props;

  const { t } = useContext(AppContext);
  const FeedBackText = React.useMemo(
    () => feedbackText && <S.Feedback>{feedbackText}</S.Feedback>,
    [feedbackText]
  );

  const NotSubscribed = React.useMemo(
    () => (
      <S.Form method="post" onSubmit={onSubmit}>
        <S.Flex>
          <S.InlineInput
            type="email"
            name="email"
            helper={FeedBackText}
            onChange={onChange}
            stacked={stacked}
            variant="rounded"
            placeholder={t('campaigns', 'newsletter.email-placeholder')}
            ariaLabel={t('campaigns', 'newsletter.email-input-aria-label')}
            // Do not send this element to CUX (cux.io)
            data-cux-exclude
          />
          <S.Button
            color={variant === 'primary' ? 'quarternary' : 'primary'}
            type="submit"
            className={buttonClass}
            disabled={loading}
            icon={loading && <Spinner position="none" isActive={loading} />}
          >
            {!loading && buttonText}
          </S.Button>
        </S.Flex>
      </S.Form>
    ),
    [
      onSubmit,
      FeedBackText,
      onChange,
      stacked,
      t,
      buttonClass,
      loading,
      buttonText,
    ]
  );

  const Subscribed = React.useMemo(
    () => (
      <S.Text>
        <S.Text>
          <S.SubscribedCheckIcon />
          <S.SubscribeTitle>{subscribeTextTitle}</S.SubscribeTitle>
        </S.Text>
        {subscribeTextSubTitle && <S.Text>{subscribeTextSubTitle}</S.Text>}
      </S.Text>
    ),
    [subscribeTextSubTitle, subscribeTextTitle]
  );

  return (
    <S.NewsLetter stacked={stacked} variant={variant} className={className}>
      {title && <S.Title>{title}</S.Title>}
      {description && <S.Text>{description}</S.Text>}
      {!isSubscribed ? NotSubscribed : Subscribed}
      <S.Legal>{legalText}</S.Legal>
    </S.NewsLetter>
  );
};

SignupForm.defaultProps = {
  buttonClass: '',
  variant: 'primary',
  stacked: false,
  email: '',
  onSubmit: noop,
  onChange: noop,
};

export default SignupForm;
